import * as React from 'react';
import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useSetEventTourCompleted } from './guidedTourHooks';
import { useNavigateWithAppName } from '../../../common/NavigateWithAppName';
import {
	blackColour,
	guidedTourToolTipButtonColor,
	guidedTourToolTipButtonSize,
	guidedTourToolTipTitleAndContentColor,
	guidedTourToolTipTitleAndContentSize,
	popupZIndex,
	whiteColour
} from '../../../cssConstants';
import useTourStore from '../../../../zustandStore/tourStore';
import useClientContextStore from '../../../../zustandStore/clientContextStore';
import { useGetClientEvent, useGetClientEventSessions } from '../../../../core/api/event';
import BottomModal from '../../../../commonComponents/bottomModal/BottomModal';
import magnifierPng from '../../../../images/magnifier.png';
import { setEventTourSkipTimestamp } from '../../../../core/localStorage';
import { buildUpdatedEventTourSteps } from './stepToLocationMap';
import { eventPageUrl, tabMenuUrlParam } from '../../../../core/constants';
import { EventDetailItems } from '../../../../core/enums';

const EventTourProvider = () => {
	const navigate = useNavigateWithAppName();
	const location = useLocation();

	const {
		tourTrigger,
		setTriggerTour,
		setCurrentPageRoute,
		lastStep,
		setLastStep,
		pageReadyMap,
		resetPageReady,
		selectedNetworkUserId
	} = useTourStore();
	const clientDetails = useClientContextStore((state) => state.clientDetails);

	const queryClient = useQueryClient();
	const { mutate: setTourCompleted } = useSetEventTourCompleted(queryClient);
	const event = useGetClientEvent();
	const eventSessions = useGetClientEventSessions(event?.eventId);

	const [run, setRun] = React.useState(false);
	const [stepIndex, setStepIndex] = React.useState(0);
	const [showEventTourEndModal, setShowEventTourEndModal] = React.useState(false);

	const updatedSteps = buildUpdatedEventTourSteps(eventSessions, selectedNetworkUserId);

	// Wait until current step's page/tab is marked ready
	const currentRoute = updatedSteps?.[stepIndex]?.route;
	const isCurrentStepReady = currentRoute ? pageReadyMap[currentRoute] === true : true;

	React.useEffect(() => {
		if (tourTrigger) {
			setTriggerTour(false);
			setStepIndex(0);
			setRun(true);
		}
	}, [tourTrigger]);

	React.useEffect(() => {
		// body height 100% causes misaligned Joyride spotlight
		document.body.style.height = run ? 'auto' : '100%';

		return () => {
			document.body.style.height = '100%';
		};
	}, [run]);

	React.useEffect(() => {
		if (currentRoute && run) {
			setCurrentPageRoute(currentRoute);

			const pathWithQueryString = `${location.pathname}${location.search}`;
			if (pathWithQueryString !== currentRoute) {
				navigate(currentRoute);
			}
		}
	}, [stepIndex, run, currentRoute]);

	const handleJoyrideCallback = (data: CallBackProps) => {
		const { status, action, index, type } = data;

		if (data.action === ACTIONS.CLOSE) {
			setRun(false);
			setShowEventTourEndModal(true);
			return;
		}

		if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
			setCurrentPageRoute(null);
			setTourCompleted();
			setRun(false);
			setStepIndex(0);
			setLastStep(index);
			navigate(`${eventPageUrl}?${tabMenuUrlParam}=${EventDetailItems.Home}`);
		} else if (type === EVENTS.STEP_AFTER) {
			// Even click Back, it will trigger this step:after event
			let newIndex = index;

			if (action === ACTIONS.NEXT) {
				newIndex = index + 1;
			} else if (action === ACTIONS.PREV) {
				newIndex = index - 1;
			}

			// Reset page readiness if next page is different before changing step
			const currentPathWithQuery = `${location.pathname}${location.search}`;
			const nextRoute = updatedSteps?.[newIndex]?.route;
			if (nextRoute && nextRoute !== currentPathWithQuery) resetPageReady(nextRoute);

			setStepIndex(newIndex);
			setLastStep(newIndex);
		}
	};

	const handleEndTour = () => {
		setShowEventTourEndModal(false);
		setEventTourSkipTimestamp();
	};

	const handleContinueTour = () => {
		setShowEventTourEndModal(false);
		setRun(true);
		setStepIndex(lastStep);
	};

	if (!updatedSteps || updatedSteps.length === 0 || clientDetails?.invitedToEvent !== true) return null;

	return (
		<>
			<Joyride
				steps={updatedSteps}
				run={run && isCurrentStepReady}
				stepIndex={stepIndex}
				continuous
				disableScrolling
				styles={{
					options: {
						zIndex: popupZIndex,
						arrowColor: whiteColour,
						backgroundColor: whiteColour,
						overlayColor: 'rgba(0, 0, 0, 0.4)',
						primaryColor: guidedTourToolTipTitleAndContentColor,
						textColor: guidedTourToolTipTitleAndContentColor,
						width: 360
					},
					tooltipTitle: {
						fontSize: guidedTourToolTipTitleAndContentSize,
						fontWeight: 500,
						color: guidedTourToolTipTitleAndContentColor,
						marginBottom: '10px',
						textAlign: 'left'
					},
					tooltipContent: {
						fontSize: guidedTourToolTipTitleAndContentSize,
						color: guidedTourToolTipTitleAndContentColor,
						lineHeight: '16px',
						letterSpacing: '0.4px',
						fontWeight: 400,
						textAlign: 'left',
						padding: '0px'
					},
					buttonBack: {
						color: guidedTourToolTipButtonColor,
						fontWeight: 500,
						fontSize: guidedTourToolTipButtonSize,
						marginRight: 'auto',
						marginLeft: 'unset',
						paddingLeft: 0
					},
					buttonNext: {
						background: 'none',
						color: guidedTourToolTipButtonColor,
						fontWeight: 500,
						fontSize: guidedTourToolTipButtonSize,
						padding: '8px'
					},
					buttonClose: {
						color: blackColour,
						fontWeight: 300,
						fontSize: '25px'
					}
				}}
				locale={{
					last: 'Done',
					next: 'Next',
					back: 'Back'
				}}
				callback={handleJoyrideCallback}
			/>
			<BottomModal
				open={showEventTourEndModal}
				onClose={() => setShowEventTourEndModal(false)}
				imageSrc={magnifierPng}
				title="End the guided tour?"
				body={['Tap the button below to end the guided tour.']}
				actions={[
					{ label: 'End tour', callback: handleEndTour },
					{ label: 'Continue tour', callback: handleContinueTour }
				]}
			/>
		</>
	);
};

export default EventTourProvider;
